import {
  Droplets, FlaskConical, Salad, Moon, Brain,
  Target, TrendingUp, Shield, Heart, Zap, Sparkles
} from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const GST_RATE = 0.18;

export const ELITE_PRICING = {
  "3 Months": { individual: 24000, couple: 40000 },
  "1 Month":  { individual: 12000, couple: 18000 },
  "6 Months": { individual: 45000, couple: 75000 },
};
export const WORKOUT_ADDON = { individual: 8000, couple: 10000 };

export function formatPrice(amount) { return amount.toLocaleString("en-IN"); }
export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
export const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.6, delay: i * 0.12 } });

export const PILLARS = [
  {
    icon: Droplets, accent: "#E8B4B8", title: "Cycle-Synced Nutrition",
    maleNote: "Spermatogenesis-Synced for the male track",
    summary: "Your hormones shift dramatically across your menstrual cycle — your nutrition should too.",
    description: "Rather than a static weekly meal plan, we adjust your nutrient intake to match the four phases of your cycle — menstrual, follicular, ovulatory, and luteal. Each phase has different demands for energy, iron, healthy fats, and anti-inflammatory support. For men, nutrition is aligned to the ~74–90 day spermatogenesis cycle to optimize sperm quality at every stage of production.",
    outcomes: [
      { icon: Target, text: "Phase-specific nutrient timing for egg and sperm quality" },
      { icon: Zap, text: "Reduced PMS symptoms, cravings, and energy crashes" },
      { icon: TrendingUp, text: "Improved ovulation predictability and cycle regularity" },
    ],
    stat: { value: "4", label: "cycle phases addressed" },
  },
  {
    icon: FlaskConical, accent: "#A8D5BA", title: "Biomarker-Informed Care",
    summary: "Every recommendation is grounded in your actual lab work — not assumptions or generic ranges.",
    description: "We review your complete panel — AMH, FSH/LH ratio, thyroid (TSH, T3, T4), fasting insulin, HbA1c, Vitamin D, B12, and ferritin — to identify exactly where your body needs support. For men, we examine testosterone, semen analysis parameters, and DNA fragmentation index. This isn't about treating numbers; it's about understanding what your biomarkers reveal about your reproductive readiness.",
    outcomes: [
      { icon: Shield, text: "Personalized plans based on your unique hormonal profile" },
      { icon: Target, text: "Early identification of insulin resistance and thyroid imbalance" },
      { icon: TrendingUp, text: "Measurable improvement in key fertility markers by Week 12" },
    ],
    stat: { value: "12+", label: "biomarkers analyzed" },
  },
  {
    icon: Salad, accent: "#C4B896", title: "Gut-Hormone Axis",
    summary: "Your gut microbiome directly controls how your body metabolizes estrogen and testosterone.",
    description: "The estrobolome — a collection of gut bacteria — regulates estrogen recycling in your body. An imbalanced gut can lead to estrogen dominance, poor nutrient absorption, and chronic low-grade inflammation that silently undermines fertility. We address digestive symptoms, food sensitivities, microbiome diversity, and fermented food intake as foundational pillars of your hormone health.",
    outcomes: [
      { icon: Heart, text: "Optimized estrogen metabolism and reduced inflammation" },
      { icon: Zap, text: "Resolved bloating, acidity, and digestive discomfort" },
      { icon: Shield, text: "Better nutrient absorption for reproductive support" },
    ],
    stat: { value: "70%", label: "of immunity lives in the gut" },
  },
  {
    icon: Moon, accent: "#B8C4E8", title: "Sleep & Circadian Rhythm",
    summary: "Disrupted sleep doesn't just make you tired — it suppresses the hormones needed for conception.",
    description: "Melatonin is a powerful antioxidant that protects egg quality. Disrupted circadian rhythms throw off the LH surge needed for ovulation, increase cortisol, and impair insulin sensitivity. We address meal timing relative to your sleep-wake cycle, light exposure habits, and sleep quality through evidence-based interventions — not just 'sleep hygiene tips.' For men, we screen for sleep apnea indicators linked to low testosterone.",
    outcomes: [
      { icon: Moon, text: "Restored melatonin production for egg protection" },
      { icon: TrendingUp, text: "Improved LH surge timing and ovulation signaling" },
      { icon: Zap, text: "Better insulin sensitivity through circadian meal timing" },
    ],
    stat: { value: "90%", label: "see sleep improvement by Week 6" },
  },
  {
    icon: Brain, accent: "#D4B8E8", title: "Stress & Cortisol-Conscious Eating",
    summary: "Chronic stress rewires your eating patterns and suppresses reproductive hormones at the source.",
    description: "When cortisol stays elevated, your body diverts resources away from reproduction. This pillar addresses the stress-fertility connection head-on — how chronic stress disrupts blood sugar, triggers emotional eating, and suppresses GnRH (the master reproductive hormone). We include exercise load management (both under- and over-training disrupt cycles), guided breathwork practices, and cortisol-conscious meal strategies.",
    outcomes: [
      { icon: Brain, text: "Reduced cortisol impact on reproductive hormone signaling" },
      { icon: Heart, text: "Guided breathwork and mindfulness for anxiety and sleep" },
      { icon: Shield, text: "Exercise load balanced to support — not suppress — fertility" },
    ],
    stat: { value: "3x", label: "fertility risk with chronic stress" },
  },
];

export const PILLAR_PREVIEWS = [
  { icon: Droplets, color: "#E8B4B8", title: "Cycle-Synced Nutrition", benefit: "Nutrient intake matched to each phase of your menstrual cycle — optimizing egg quality, reducing PMS, and supporting regular ovulation." },
  { icon: FlaskConical, color: "#A8D5BA", title: "Biomarker-Informed Care", benefit: "Your plan is built on your actual lab results — AMH, thyroid, insulin, Vitamin D, B12 — not generic guidelines or guesswork." },
  { icon: Salad, color: "#C4B896", title: "Gut-Hormone Axis", benefit: "Restore the estrobolome — the gut bacteria that regulate estrogen metabolism — to reduce inflammation and improve hormone balance." },
  { icon: Moon, color: "#B8C4E8", title: "Sleep & Circadian Rhythm", benefit: "Protect egg quality through melatonin optimization, fix circadian meal timing, and restore the LH surge needed for ovulation." },
  { icon: Brain, color: "#D4B8E8", title: "Stress & Cortisol Balance", benefit: "Reclaim fertility from chronic stress — guided breathwork, cortisol-conscious meal strategies, and exercise load management." },
];

export const PHASES = [
  { phase: "01", title: "Precision Onboarding", weeks: "Week 1", description: "Comprehensive intake assessment across all five pillars plus a full biomarker review with your dietitian. This shapes your personalized plan from day one.", color: "#D4AF37" },
  { phase: "02", title: "Guided Focus Weeks", weeks: "Weeks 2–9", description: "Each week covers one nutrition pillar — hydration, protein, iron, healthy fats, omega-3s, fiber, folate, calcium, zinc — with a live group coaching call.", color: "#A8D5BA" },
  { phase: "03", title: "Lifestyle Integration", weeks: "Weeks 10–11", description: "Blood sugar balance, physical activity, and mindful eating woven together with everything learned so far.", color: "#B8C4E8" },
  { phase: "04", title: "Outcomes Review", weeks: "Week 12", description: "Progress review — how adherence, nutrition literacy, and biomarkers have moved since Week 1.", color: "#E8B4B8" },
];

export const WEEKLY_FOCUS = [
  { week: 1, focus: "Hydration", icon: Droplets },
  { week: 2, focus: "Protein Adequacy", icon: Zap },
  { week: 3, focus: "Iron-Rich Foods", icon: Shield },
  { week: 4, focus: "Healthy Fats", icon: Heart },
  { week: 5, focus: "Omega-3 Rich Foods", icon: Sparkles },
  { week: 6, focus: "Fiber & Gut Health", icon: Salad },
  { week: 7, focus: "Folate & B-Vitamins", icon: FlaskConical },
  { week: 8, focus: "Calcium & Vitamin D", icon: Moon },
  { week: 9, focus: "Zinc & Antioxidants", icon: Target },
];

export const CARE_TEAM = [
  { name: "Revathy R.P", role: "Clinical Nutritionist, 10+ Yrs Experience, Certified in Fertility Nutrition", highlight: true },
  { name: "Dr. Neelima Paleru", role: "Gynaecologist" },
  { name: "Dr. Sumageethika", role: "Gynaecologist" },
  { name: "Dr. Fahim", role: "Andrologist (MCh Urology, FAMH Fellowship — India & USA)" },
  { name: "Dr. Uma", role: "Diabetologist (MD General Medicine)" },
  { name: "Dr. Rekha", role: "Fertility Ayurveda Specialist (BAMS, 12+ years)" },
];
